const getRouteForDirectionsFromStops = (stops) => {
  if (!stops?.length) {
    return null;
  }

  const firstStop = stops[0];
  const lastStop = stops[stops.length - 1];

  if (!window?.google?.maps) {
    return null;
  }

  const origin = new window.google.maps.LatLng(firstStop.latitude, firstStop.longitude);
  const destination = new window.google.maps.LatLng(lastStop.latitude, lastStop.longitude);
  const waypoints = stops
    .map((stop, key) => {
      if (key === 0 || key === stops.length - 1) {
        return null;
      }

      return {
        location: new window.google.maps.LatLng(stop.latitude, stop.longitude),
        stopover: true,
      };
    })
    .filter((notNull) => notNull);

  return {
    avoidFerries: true,
    origin,
    destination,
    waypoints,
    optimizeWaypoints: false,
    travelMode: window.google.maps.TravelMode.DRIVING,
  };
};

const getDirections = (directionsRequest) => {
  const DirectionsService = new window.google.maps.DirectionsService();

  return new Promise((resolve, reject) => {
    DirectionsService.route(directionsRequest, (result, status) => {
      if (status !== window.google.maps.DirectionsStatus.OK) {
        reject();
      }

      resolve(result);
    });
  });
};

const convertStopsToMaker = (stops) => {
  return stops?.map((stop) => ({
    id: stop.id,
    stop_type: stop.stop_type,
    latitude: stop.address.latitude,
    longitude: stop.address.longitude,
  }));
};

const getStopStringForMemoize = (stops = []) => {
  return stops?.map((stop) => `${stop.stop_type}${stop.latitude}${stop.longitude}`).join('|');
};

const rawStopMarkerPath =
  'M10.0010719,0 C15.5233274,0 20,4.39805982 20,9.82334156 C20,11.3679794 19.6292358,12.8908482 18.917809,14.2683088 L11.1509796,29.3064017 C10.9312182,29.7319026 10.4867429,30 10.0010719,30 C9.51540089,30 9.07092562,29.7319026 8.85116418,29.3064017 L1.08433477,14.2683088 C-1.41443255,9.43020911 0.552075496,3.51807476 5.47665343,1.06318713 C6.87873535,0.364253416 8.42882454,0 10.0010719,0 Z';

const colorMapByStopType = (colorSchema, stopType) => {
  const config = {
    PICKUP: {
      strokeColor: colorSchema.primary,
      fillColor: colorSchema.primary,
    },
    DROPOFF: {
      strokeColor: colorSchema.primary,
      fillColor: colorSchema.white,
    },
  };
  return config[stopType];
};

const labelPropsByStop = (colorSchema, stop, text) => {
  const stopType = stop.stop_type;

  const baseLabelStyle = {
    fontFamily: 'Roboto, Verdana, sans-serif',
    fontWeight: 'bold',
    fontSize: '16px',
  };

  const labelStyle = {
    PICKUP: {
      ...baseLabelStyle,
      color: 'white',
    },
    DROPOFF: {
      ...baseLabelStyle,
      color: colorSchema.primary,
    },
  };

  return {
    label: {
      text,
      ...labelStyle[stopType],
    },
  };
};

const getIcon = (colorSchema, stop) => {
  const colorProps = colorMapByStopType(colorSchema, stop.stop_type);

  return {
    path: rawStopMarkerPath,
    ...colorProps,
    strokeWeight: 2,
    strokeOpacity: 1,
    fillOpacity: 1,
    anchor: new window.google.maps.Point(10, 30),
    labelOrigin: new window.google.maps.Point(10, 12),
  };
};

const getBoundsWithStops = (stops) => {
  const googleBounds = new window.google.maps.LatLngBounds();
  stops.reduce((_, stop) => {
    const p = new window.google.maps.LatLng(stop?.latitude, stop?.longitude);
    googleBounds.extend(p);
    return googleBounds;
  }, googleBounds);

  return googleBounds;
};

const getRoutedMarkerProps = (colorSchema, stop, key) => ({
  icon: getIcon(colorSchema, stop),
  ...labelPropsByStop(colorSchema, stop, `${key + 1}`),
});

const getNonRoutedMarkerProps = (colorSchema, stop) => ({
  icon: getIcon(colorSchema, stop),
  ...labelPropsByStop(colorSchema, stop, (stop.stop_type || '').charAt(0)),
});

const getDirectionsRendererOptions = (color, options = {}) => {
  const { showIcons = true } = options;
  return {
    markerOptions: {
      visible: false,
    },
    suppressInfoWindows: true,
    preserveViewport: true,
    polylineOptions: {
      clickable: false,
      strokeColor: color,
      strokeWeight: 2,
      strokeOpacity: 1,
      zIndex: 3,
      icons: showIcons
        ? [
            ...[25, 50, 75].map((value) => ({
              icon: {
                path: 'M0 8.284c0-.43.185-.84.508-1.125l7.5-6.617a1.5 1.5 0 0 1 1.985 0l7.5 6.618a1.5 1.5 0 0 1-.993 2.624h-15a1.5 1.5 0 0 1-1.5-1.5z',
                strokeColor: color,
                strokeOpacity: 1,
                fillColor: color,
                fillOpacity: 1,
                scale: 1,
                anchor: new window.google.maps.Point(10, 0),
              },
              offset: `${value}%`,
            })),
          ]
        : [],
    },
  };
};

const filterAlreadyExistingInRouteStops = (nonRoutedStops = [], routedStops = []) => {
  const routedStopsIds = routedStops.map((routedStop) => routedStop.id);

  return nonRoutedStops.filter((nonRoutedStop) => !routedStopsIds.includes(nonRoutedStop.id));
};

export {
  convertStopsToMaker,
  getRouteForDirectionsFromStops,
  getDirections,
  getStopStringForMemoize,
  getRoutedMarkerProps,
  getBoundsWithStops,
  getDirectionsRendererOptions,
  getNonRoutedMarkerProps,
  filterAlreadyExistingInRouteStops,
};
